import { useCallback, useEffect, useState } from 'react';
import { useApi, useClient } from './provider';
import { isLocalShell } from './useResources';

export type PushState = 'unsupported' | 'needs_install' | 'disabled_on_server' | 'denied' | 'off' | 'on' | 'busy';

const b64urlToBytes = (s: string): Uint8Array => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};
/** Running as an installed home-screen app (standalone display mode; iOS exposes navigator.standalone). */
export const isStandalone = (): boolean =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
const isIOS = (): boolean => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

/**
 * Web Push for this browser/phone. Cloud only. On iOS push works only once the page is added to the home screen
 * (16.4+), so `needs_install` tells the UI to show the Add-to-Home-Screen hint first.
 */
export function usePush() {
  const api = useApi();
  const client = useClient();
  const cloud = !isLocalShell(client);
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [state, setState] = useState<PushState>(supported ? 'off' : 'unsupported');
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!supported || !cloud) return;
    if (isIOS() && !isStandalone()) {
      setState('needs_install');
      return;
    }
    try {
      const v = await api.pushVapidKey();
      if (!v.enabled) return setState('disabled_on_server');
      if (Notification.permission === 'denied') return setState('denied');
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      setState(sub ? 'on' : 'off');
    } catch (e) {
      setError((e as Error).message);
      setState('off');
    }
  }, [api, cloud, supported]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported || !cloud) return;
    setState('busy');
    setError(null);
    try {
      const v = await api.pushVapidKey();
      if (!v.enabled) return setState('disabled_on_server');
      if ((await Notification.requestPermission()) !== 'granted') return setState('denied');
      const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'));
      await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(v.public_key) as BufferSource }));
      const json = sub.toJSON();
      await api.pushSubscribe({ endpoint: sub.endpoint, keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' }, user_agent: navigator.userAgent.slice(0, 200) });
      setState('on');
    } catch (e) {
      setError((e as Error).message);
      setState('off');
    }
  }, [api, cloud, supported]);

  const disable = useCallback(async () => {
    if (!supported || !cloud) return;
    setState('busy');
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
        await sub.unsubscribe();
      }
      setState('off');
    } catch (e) {
      setError((e as Error).message);
      setState('on');
    }
  }, [api, cloud, supported]);

  return { state, supported, cloud, error, enable, disable, refresh, standalone: isStandalone(), ios: isIOS() };
}
